import React, { useState, useEffect } from 'react';
import { Modal, Button } from '../../../../components/ui';
import { FiAlertCircle as AlertCircle } from 'react-icons/fi';
import { apiCall } from '../../../../services/realApi';

interface UserData {
  id: string;
  name?: string;
  email: string;
  clientRole?: string;
  client_role?: string;
}

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  user: UserData | null;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, onClose, onSuccess, user }) => {
  const [clientRole, setClientRole] = useState('regular_user');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && user) {
      setClientRole(user.clientRole || user.client_role || 'regular_user');
      setError('');
    }
  }, [isOpen, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await apiCall(`/users/${user!.id}/client-role`, {
        method: 'PUT',
        body: JSON.stringify({ client_role: clientRole })
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: unknown) {
      console.error('Error updating user role:', err);
      setError(err instanceof Error ? err.message : 'Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit User Role"
      maxWidth="max-w-md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* User Info (read-only) */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {user.name || 'Unknown User'}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {user.email}
          </div>
        </div>

        {/* Client Role */}
        <div>
          <label htmlFor="client-role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Client Role
          </label>
          <select id="client-role"
            value={clientRole}
            onChange={(e) => setClientRole(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="regular_user">Regular User</option>
            <option value="admin_user">Admin User</option>
          </select>
          <div className="mt-2 space-y-1 text-xs text-gray-500 dark:text-gray-400">
            <p><span className="font-semibold">Admin User:</span> Can manage users and organization settings</p>
            <p><span className="font-semibold">Regular User:</span> Standard access to assessments and features</p>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
          >
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditUserModal;
